
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { getOrderStatus, getOrderType, getDisplayOrderNumber } from '@/utils/getters';
import Toast from '@/utils/toast';
import ExpansionPanel from '@/components/shared/suites/panels/ExpansionPanel.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import OrderCartItem from './OrderCartItem.vue';
import OrderCartPricing from './OrderCartPricing.vue';
import OrderInfo from '@/components/order-management/OrderInfo.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace = 'orders';

@Component<OrderCart>({
	components: {
		ExpansionPanel,
		IconButton,
		InfoBanner,
		OrderCartItem,
		OrderCartPricing,
		OrderInfo,
		TextButton
	}
})
export default class OrderCart extends Vue {
	@Action('validateCart', { namespace }) validateCart!: () => void;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Getter('getOrderType', { namespace }) private orderType!: string;
	@Getter('getCart', { namespace }) private cart!: Cart;
	@Getter('getCosts', { namespace }) private costs!: Costs;
	@Getter('getModifyingOrder', { namespace }) private modifyingOrder!: boolean;
	@Getter('isQuoting', { namespace }) private isQuoting!: boolean;
	@Getter('getSelectedOrder', { namespace }) private order!: Order;
	@Getter('getRestaurant', { namespace: 'auth' }) restaurant!: Restaurant;
	@Getter('areOrdersPaused', { namespace: 'auth' }) private areOrdersPaused!: boolean;
	@Getter('getRepeatOrderNotices', { namespace }) private repeatOrderNotices!: RepeatOrderNotices | null;
	@Prop({ type: Boolean, default: false }) private isOpen!: boolean;
	@Prop({ type: Boolean, default: false }) private isUserFromAPP!: boolean;
	@Prop({ type: Boolean, default: false }) private isTableInfoFetchedByApp!: boolean;
	@Prop({ type: Boolean, default: false }) private kioskMode!: boolean;

	private dismissedNotices: (keyof RepeatOrderNotices)[] = [];

	private get orderTypeLocalized(): string {
		if (this.isQuoting) {
			return this.$t('orders.cart.header_new_quote');
		}
		if (!this.orderType) {
			return this.$t('orders.cart.header_no_order_type');
		}

		// BOWLERO ONLY - They have different order type naming conventions...
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		if(this.restaurant.group === 'bowlero' || this.restaurant.groups?.includes('bowlero')) {
			if(this.orderType === 'dine-in' && this.orderDetails.menuManagerEvent) {
				return this.$t('orders.cart.header_event');
			}
			if(this.orderType === 'dine-in' && this.orderDetails.menuManagerLaneReservation) {
				return this.$t('orders.cart.header_lane_res');
			}
			else if(this.orderType === 'dine-in') {
				return this.$t('orders.cart.header_retail');
			}
		}
		return this.$t(`orders.cart.header_${this.orderType.replace('-', '_')}`);
	}

	private get orderTypeInfo(): ParentOrderType {
		return getOrderType(this.order);
	}

	private get orderNumber(): string {
		return getDisplayOrderNumber(this.order.id, this.order.restaurant_id);
	}

	private get orderStatus(): string {
		return getOrderStatus(this.order.status);
	}

	private get repeatOrderBanners(): RepeatOrderBanner[] {
		const notices: RepeatOrderNotices | null = this.repeatOrderNotices;
		if (!notices) {
			return [];
		}
		const priceChanges: string = notices.repriced
			.map((change: RepeatOrderPriceChange) => `${change.name} — ${this.$n(change.oldPrice, 'currency')} → ${this.$n(change.newPrice, 'currency')}`)
			.join(', ');
		const banners: RepeatOrderBanner[] = [
			{ key: 'removed', text: this.$tc('orders.repeat_order.removed_items', notices.removed.length, { count: notices.removed.length, names: this.formatNameList(notices.removed) }) },
			{ key: 'removedModifiers', text: this.$tc('orders.repeat_order.removed_modifiers', notices.removedModifiers.length, { count: notices.removedModifiers.length, names: this.formatNameList(notices.removedModifiers) }) },
			{ key: 'failedToAdd', text: this.$tc('orders.repeat_order.add_failed', notices.failedToAdd.length, { count: notices.failedToAdd.length, names: this.formatNameList(notices.failedToAdd) }) },
			{ key: 'repriced', text: this.$tc('orders.repeat_order.price_changed', notices.repriced.length, { count: notices.repriced.length, details: priceChanges }) }
		];
		return banners.filter((banner: RepeatOrderBanner) => notices[banner.key].length && !this.dismissedNotices.includes(banner.key));
	}

	private async handleCartValidation(): Promise<void> {
		if (this.cart.items.length) {
			try {
				await this.validateCart();
				this.$emit('open-guest-details');
			}
			catch (errorMessage) {
				Toast.error(this.$t('orders.cart.cart_validation_error', { errorMessage }));
			}
		}
		else {
			Toast.error(this.$t(this.isQuoting ? 'orders.cart.empty_quote_error' : 'orders.cart.empty_order_error'));
		}
	}

	/**
	 * Join names into a sentence list, e.g. "Grilled Salmon Platter and Chocolate Lava Cake".
	 *
	 * @param {string[]} names - the names to join
	 * @return {string}
	 */
	private formatNameList(names: string[]): string {
		if (names.length <= 1) {
			return names[0] || '';
		}
		return `${names.slice(0, -1).join(', ')} ${this.$t('orders.repeat_order.list_and')} ${names[names.length - 1]}`;
	}
}
